import { getObjectKeysAsObject } from "@checkout/helpers";
import { ParentState } from "@checkout/types";
import { WalletVoucherStatusEnum } from "@hopper-b2b/types";
import { assign, type DoneInvokeEvent } from "xstate";
import type {
  ApplyWalletCreditEvent,
  ApplyWalletOfferEvent,
  SetWalletCreditAmountEvent,
  SetWalletCreditBalanceEvent,
  SetWalletOfferErrorEvent,
} from "./events";
import type { EligibleOffersResult, WalletInitResult } from "./types";
import {
  OfferPaymentState,
  TravelCreditPaymentState,
  type WalletPaymentParentContext,
} from "./types";
import {
  applyCreditToCartUpdateParams,
  applyOfferToCartUpdateParams,
  findBestOffer,
  findCreditPaymentId,
  findOfferPaymentId,
  removeCreditFromCartUpdateParams,
  removeOfferFromCartUpdateParams,
} from "./utils";

export const actions = {
  setWalletCreditPayAmount: assign(
    (ctx: WalletPaymentParentContext, event: SetWalletCreditAmountEvent) => {
      ctx[TravelCreditPaymentState].creditPaymentAmount = event.amount;
      return ctx;
    }
  ),
  setWalletCreditBalance: assign(
    (ctx: WalletPaymentParentContext, event: SetWalletCreditBalanceEvent) => {
      ctx[TravelCreditPaymentState].creditBalance = event.value;
      return ctx;
    }
  ),
  setWalletOffers: assign(
    (
      context: WalletPaymentParentContext,
      event: DoneInvokeEvent<EligibleOffersResult>
    ) => {
      const { offers, roomOfferId } = event.data;
      context[OfferPaymentState].visited = true;
      context[OfferPaymentState].fetchedOffers = true;
      if (offers.length > 0) {
        context[OfferPaymentState].offers = offers;
        context[OfferPaymentState].selectedOffer = findBestOffer(
          offers,
          roomOfferId
        );
        context[OfferPaymentState].offerApplied = true;
        context[OfferPaymentState].offerStatus =
          WalletVoucherStatusEnum.DEFAULT_OPT_IN;
      }

      return context;
    }
  ),
  /**
   * Combined init: sets offers + credit balance from a single service call.
   * Both get added to the cart in the cartUpdate entry action that follows.
   */
  setWalletInitResult: assign(
    (
      context: WalletPaymentParentContext,
      event: DoneInvokeEvent<WalletInitResult>
    ) => {
      const { offers, roomOfferId, creditBalance } = event.data;

      // Offers
      context[OfferPaymentState].visited = true;
      context[OfferPaymentState].fetchedOffers = true;
      if (offers.length > 0) {
        context[OfferPaymentState].offers = offers;
        context[OfferPaymentState].selectedOffer = findBestOffer(
          offers,
          roomOfferId
        );
        context[OfferPaymentState].offerApplied = true;
        context[OfferPaymentState].offerStatus =
          WalletVoucherStatusEnum.DEFAULT_OPT_IN;
      }

      // Credits — pre-apply max if balance is available
      if (creditBalance != null && creditBalance.value > 0) {
        context[TravelCreditPaymentState].creditBalance = creditBalance.value;
        context[TravelCreditPaymentState].creditPaymentAmount = creditBalance;
        context[TravelCreditPaymentState].creditInitialized = true;
      }

      return context;
    }
  ),
  setWalletOfferError: assign(
    (ctx: WalletPaymentParentContext, event: SetWalletOfferErrorEvent) => {
      ctx[OfferPaymentState].error = event.error;
      return ctx;
    }
  ),
  applyWalletOffer: assign(
    (ctx: WalletPaymentParentContext, event: ApplyWalletOfferEvent) => {
      const offerState = ctx[OfferPaymentState];
      const offer = offerState.offers?.find((o) => o.id === event.offerId);
      if (offer == null) {
        return ctx;
      }

      // Single applied offer: swapping removes the previous one in the same
      // quote/update.
      const previousOffer = offerState.selectedOffer;
      let cartUpdate = ctx[ParentState.cartUpdate];
      if (
        previousOffer != null &&
        previousOffer.id !== offer.id &&
        offerState.offerApplied
      ) {
        // Use the BE-assigned payment ID from the breakdown, not the offer JWT.
        const paymentId =
          findOfferPaymentId(ctx[ParentState.cartQuote].quoteBreakdown) ??
          previousOffer.id;
        cartUpdate = removeOfferFromCartUpdateParams(cartUpdate, paymentId, {
          isApplied: true,
        });
      }

      ctx[ParentState.cartUpdate] = applyOfferToCartUpdateParams(
        cartUpdate,
        offer.id
      );
      offerState.selectedOffer = offer;
      offerState.offerApplied = true;
      offerState.offerStatus = WalletVoucherStatusEnum.EXPLICITLY_ACCEPTED;
      return ctx;
    }
  ),
  removeWalletOffer: assign((ctx: WalletPaymentParentContext) => {
    const offerState = ctx[OfferPaymentState];
    const offer = offerState.selectedOffer;
    if (offer == null) {
      return ctx;
    }

    // Use the BE-assigned payment ID from the breakdown, not the offer JWT.
    const paymentId =
      findOfferPaymentId(ctx[ParentState.cartQuote].quoteBreakdown) ?? offer.id;

    ctx[ParentState.cartUpdate] = removeOfferFromCartUpdateParams(
      ctx[ParentState.cartUpdate],
      paymentId,
      { isApplied: Boolean(offerState.offerApplied) }
    );
    // selectedOffer is kept for tracking only (which offer was declined);
    // consumers must gate on offerApplied — see getAppliedWalletOffer.
    offerState.offerApplied = false;
    offerState.offerStatus = WalletVoucherStatusEnum.EXPLICITLY_DECLINED;
    return ctx;
  }),
  /**
   * cartUpdate entry action: auto-applies the selected offer on the initial
   * pass. No-op if off, unapplied, or already queued.
   */
  addAppliedWalletOfferToCartUpdate: assign(
    (ctx: WalletPaymentParentContext) => {
      const offerState = ctx[OfferPaymentState];
      const offer = offerState?.selectedOffer;
      if (
        !ctx.featureFlags?.enableWalletOffers ||
        !offerState?.offerApplied ||
        offer == null
      ) {
        return ctx;
      }

      ctx[ParentState.cartUpdate] = applyOfferToCartUpdateParams(
        ctx[ParentState.cartUpdate],
        offer.id
      );
      return ctx;
    }
  ),
  applyWalletCredit: assign(
    (ctx: WalletPaymentParentContext, event: ApplyWalletCreditEvent) => {
      const creditState = ctx[TravelCreditPaymentState];
      creditState.creditPaymentAmount = event.amount;
      creditState.creditInitialized = true;

      // Remove a previously applied credit before adding the new one
      const existingId = findCreditPaymentId(
        ctx[ParentState.cartQuote].quoteBreakdown
      );
      let cartUpdate = ctx[ParentState.cartUpdate];
      if (existingId != null) {
        cartUpdate = removeCreditFromCartUpdateParams(cartUpdate, existingId, {
          isApplied: true,
        });
      }

      ctx[ParentState.cartUpdate] = applyCreditToCartUpdateParams(
        cartUpdate,
        event.amount
      );
      return ctx;
    }
  ),
  removeWalletCredit: assign((ctx: WalletPaymentParentContext) => {
    const creditState = ctx[TravelCreditPaymentState];
    creditState.creditPaymentAmount = undefined;
    creditState.creditInitialized = true;

    const paymentId = findCreditPaymentId(
      ctx[ParentState.cartQuote].quoteBreakdown
    );

    if (paymentId != null) {
      ctx[ParentState.cartUpdate] = removeCreditFromCartUpdateParams(
        ctx[ParentState.cartUpdate],
        paymentId,
        { isApplied: true }
      );
    } else {
      // Drop any pending add
      ctx[ParentState.cartUpdate] = removeCreditFromCartUpdateParams(
        ctx[ParentState.cartUpdate],
        "",
        { isApplied: false }
      );
    }

    return ctx;
  }),
  /**
   * cartUpdate entry action: auto-applies the selected credit on the initial
   * pass. No-op if no credit selected or already queued.
   */
  addAppliedWalletCreditToCartUpdate: assign(
    (ctx: WalletPaymentParentContext) => {
      const creditState = ctx[TravelCreditPaymentState];
      if (
        creditState?.creditPaymentAmount == null ||
        creditState.creditPaymentAmount.value <= 0
      ) {
        return ctx;
      }

      // Cap credit to the quoted trip total so we never send more than
      // the booking costs (the hook caps later interactions, but the
      // initial auto-apply from setWalletInitResult uses the full balance).
      const quoteBreakdown = ctx[ParentState.cartQuote]?.quoteBreakdown;
      const tripTotalFiat = (quoteBreakdown?.sellAt ?? quoteBreakdown?.balance)
        ?.fiat?.value;
      const cappedValue =
        tripTotalFiat != null
          ? Math.min(creditState.creditPaymentAmount.value, tripTotalFiat)
          : creditState.creditPaymentAmount.value;

      const cappedAmount = {
        ...creditState.creditPaymentAmount,
        value: cappedValue,
      };
      creditState.creditPaymentAmount = cappedAmount;

      ctx[ParentState.cartUpdate] = applyCreditToCartUpdateParams(
        ctx[ParentState.cartUpdate],
        cappedAmount
      );
      return ctx;
    }
  ),
};

export const ActionTypes = getObjectKeysAsObject(actions);
